/**
 * @file: useChatSearch.ts
 *
 * The chat-search query state and result computation: matches the query
 * against thread titles/previews and contact names/titles, dropping a
 * contact already represented by a private thread. Returns the live result
 * list; the page renders it.
 */

import { useChatContacts } from '@euler/app/flows/chat/lib/contacts/context';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useChatThreads } from '@euler/app/flows/chat/lib/threads/context';
import { chatThreadTitle } from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { ChatContact, ChatThreadItem } from '@euler/model/chat';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useMemo, useState } from 'react';

export type SearchResult =
  | { kind: 'thread'; data: ChatThreadItem; displayName: string }
  | { kind: 'contact'; data: ChatContact };

export interface ChatSearch {
  query: string;
  setQuery: (q: string) => void;
  results: SearchResult[];
}

export function useChatSearch(): ChatSearch {
  const { chatThreads$ } = useChatThreads();
  const { chatContacts$ } = useChatContacts();
  const { me } = useChatSession();
  const selfName = me.user.name;
  const [threads] = useBehaviorSubject(chatThreads$);
  const [contacts] = useBehaviorSubject(chatContacts$);
  const [query, setQuery] = useState('');

  const results: SearchResult[] = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];

    const threadResults: SearchResult[] = threads
      .map(t => ({
        kind: 'thread' as const,
        data: t,
        displayName: chatThreadTitle(t, selfName),
      }))
      .filter(r => {
        if (r.displayName.toLowerCase().includes(q)) return true;
        const preview = r.data.latestMessage?.preview ?? '';
        return preview.toLowerCase().includes(q);
      });

    // Drop a contact already represented by a private thread (by peer
    // platform user id).
    const threadPeerIds = new Set(
      threads
        .filter(t => t.room?.type === 'private' && t.peer)
        .map(t => String(t.peer!.platformUserId)),
    );

    const contactResults: SearchResult[] = contacts
      .filter(c => !threadPeerIds.has(String(c.platformUserId)))
      .filter(
        c =>
          c.name.toLowerCase().includes(q) ||
          (c.jobTitle ?? '').toLowerCase().includes(q),
      )
      .map(c => ({ kind: 'contact' as const, data: c }));

    return [...threadResults, ...contactResults];
  }, [query, threads, contacts, selfName]);

  return { query, setQuery, results };
}
